/**
 * FarmSettings.tsx
 * Manages farm configuration settings including general information,
 * notifications, preferences, and security settings. Provides a user interface
 * for viewing and updating farm-related settings.
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DetailLayout from './layout/DetailLayout';
import { supabase } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import { FarmContext } from '../../../pages/farm/FarmDetails';

/**
 * Props interface for the FarmSettings component
 * @property {any} farm - Farm object
 * @property {function} onDelete - Optional delete handler
 */
interface FarmSettingsProps {
  farm: any;
  onDelete?: () => void;
}

/**
 * Interface defining the structure of all farm settings
 */
interface FarmSettings {
  general: {
    name: string;          // Farm name
    description: string;   // Farm description
    location: string;      // Farm location
    size: number;         // Farm size value
    sizeUnit: 'acres' | 'hectares';  // Unit for farm size
    timezone: string;     // Farm's timezone
  };
  notifications: {
    emailAlerts: boolean;      // Email notification preference
    smsAlerts: boolean;        // SMS notification preference
    weatherAlerts: boolean;    // Weather alert preference
    taskReminders: boolean;    // Task reminder preference
    inventoryAlerts: boolean;  // Inventory alert preference
  };
  preferences: {
    currency: string;      // Preferred currency
    language: string;      // Interface language
    dateFormat: string;    // Date display format
    measurementSystem: 'metric' | 'imperial';  // Measurement system
  };
  security: {
    twoFactorAuth: boolean;   // 2FA status
    activityLog: boolean;     // Activity logging preference
    accessControl: boolean;   // Access control status
  };
  privacy: {
    isPublic: boolean;
    showWorkers: boolean;
    showStats: boolean;
  };
  storage: {
    autoBackup: boolean;
    retentionDays: number;
  };
}

/**
 * Styled card component that maintains consistent height and layout
 */
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiCardContent-root': {
    height: '100%',
  },
}));

/**
 * Styled section component for grouping related settings
 */
const SettingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
}));

/**
 * Available currency options for the farm
 */
const currencies = [
  { code: 'KES', label: 'Kenyan Shilling (KES)' },
  { code: 'USD', label: 'US Dollar (USD)' },
  { code: 'EUR', label: 'Euro (EUR)' },
  { code: 'GBP', label: 'British Pound (GBP)' },
];

/**
 * Available language options for the interface
 */
const languages = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Swahili' },
];

/**
 * Available date format options
 */
const dateFormats = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

/**
 * Available timezone options
 */
const timezones = [
  'Africa/Nairobi',
  'UTC',
  'UTC+1',
  'UTC+2',
  'UTC+3',
];

/**
 * FarmSettings Component
 * Provides a user interface for managing farm settings and preferences.
 * Includes sections for general information, notifications, preferences,
 * and security settings.
 * 
 * @param {FarmSettingsProps} props - Component props
 * @returns {JSX.Element} - Rendered settings interface
 */
const FarmSettings: React.FC<FarmSettingsProps> = ({ farm, onDelete }) => {
  const { user } = useAuth();
  const { refreshFarm } = useContext(FarmContext) || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!farm) return <div>Loading farm data...</div>;
  const farmId = farm.id;
  // Initialize settings state with farm values
  const [settings, setSettings] = useState<FarmSettings>({
    general: {
      name: farm.name || '',
      description: farm.description || '',
      location: farm.location || '',
      size: farm.size || 0,
      sizeUnit: farm.size_unit || 'acres',
      timezone: farm.timezone || 'Africa/Nairobi',
    },
    notifications: farm.notifications || {
      emailAlerts: true,
      smsAlerts: true,
      weatherAlerts: true,
      taskReminders: true,
      inventoryAlerts: true,
    },
    preferences: farm.preferences || {
      currency: farm.currency || 'KES',
      language: farm.language || 'en',
      dateFormat: farm.date_format || 'DD/MM/YYYY',
      measurementSystem: farm.measurement_system || 'metric',
    },
    security: farm.security || {
      twoFactorAuth: false,
      activityLog: true,
      accessControl: true,
    },
    privacy: farm.privacy || {
      isPublic: false,
      showWorkers: true,
      showStats: true,
    },
    storage: farm.storage || {
      autoBackup: true,
      retentionDays: 30,
    },
  });

  // Sync settings state with farm prop changes
  useEffect(() => {
    setSettings({
      general: {
        name: farm.name || '',
        description: farm.description || '',
        location: farm.location || '',
        size: farm.size || 0,
        sizeUnit: farm.size_unit || 'acres',
        timezone: farm.timezone || 'Africa/Nairobi',
      },
      notifications: farm.notifications || {
        emailAlerts: true,
        smsAlerts: true,
        weatherAlerts: true,
        taskReminders: true,
        inventoryAlerts: true,
      },
      preferences: farm.preferences || {
        currency: farm.currency || 'KES',
        language: farm.language || 'en',
        dateFormat: farm.date_format || 'DD/MM/YYYY',
        measurementSystem: farm.measurement_system || 'metric',
      },
      security: farm.security || {
        twoFactorAuth: false,
        activityLog: true,
        accessControl: true,
      },
      privacy: farm.privacy || {
        isPublic: false,
        showWorkers: true,
        showStats: true,
      },
      storage: farm.storage || {
        autoBackup: true,
        retentionDays: 30,
      },
    });
  }, [farm]);

  // State for managing save operation feedback
  const [saveStatus, setSaveStatus] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: '',
  });

  /**
   * Handles changes to general settings fields
   * @param field - The field being updated
   * @param value - The new value for the field
   */
  const handleGeneralChange = (field: keyof FarmSettings['general'], value: string | number) => {
    setSettings({
      ...settings,
      general: {
        ...settings.general,
        [field]: value,
      },
    });
  };

  /**
   * Handles changes to notification settings
   * @param field - The notification setting being toggled
   */
  const handleNotificationChange = (field: keyof FarmSettings['notifications']) => {
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [field]: !settings.notifications[field],
      },
    });
  };

  /**
   * Handles changes to preference settings
   * @param field - The preference being updated
   * @param value - The new value for the preference
   */
  const handlePreferenceChange = (field: keyof FarmSettings['preferences'], value: string) => {
    setSettings({
      ...settings,
      preferences: {
        ...settings.preferences,
        [field]: value,
      },
    });
  };

  /**
   * Handles changes to security settings
   * @param field - The security setting being toggled
   */
  const handleSecurityChange = (field: keyof FarmSettings['security']) => {
    setSettings({
      ...settings,
      security: {
        ...settings.security,
        [field]: !settings.security[field],
      },
    });
  };

  /**
   * Handles changes to privacy settings
   * @param field - The privacy setting being toggled
   * @param value - The new value for the privacy setting
   */
  const handlePrivacyChange = (field: keyof FarmSettings['privacy'], value: boolean) => {
    setSettings({
      ...settings,
      privacy: {
        ...settings.privacy,
        [field]: value,
      },
    });
  };

  /**
   * Handles changes to storage settings
   * @param field - The storage setting being toggled or updated
   * @param value - The new value for the storage setting
   */
  const handleStorageChange = (field: keyof FarmSettings['storage'], value: boolean | number) => {
    setSettings({
      ...settings,
      storage: {
        ...settings.storage,
        [field]: value,
      },
    });
  };

  /**
   * Handles the save operation for all settings
   * Shows a success message and automatically hides it after 3 seconds
   */
  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const updateData: any = {
        name: settings.general.name,
        description: settings.general.description,
        location: settings.general.location,
        size: settings.general.size,
        size_unit: settings.general.sizeUnit,
        timezone: settings.general.timezone,
        notifications: settings.notifications,
        security: settings.security,
        // Flatten preferences into separate columns
        currency: settings.preferences.currency,
        language: settings.preferences.language,
        date_format: settings.preferences.dateFormat,
        measurement_system: settings.preferences.measurementSystem,
        // Add privacy and storage as JSONB
        privacy: settings.privacy,
        storage: settings.storage,
      };
      const { error: updateError } = await supabase
        .from('farms')
        .update(updateData)
        .eq('id', farmId)
        .eq('user_id', user?.id);
      if (updateError) throw updateError;
    setSaveStatus({
      show: true,
      type: 'success',
      message: 'Settings saved successfully',
    });
      if (refreshFarm) await refreshFarm();
    } catch (err: any) {
      setError(err.message || 'Failed to update farm settings.');
      setSaveStatus({
        show: true,
        type: 'error',
        message: err.message || 'Failed to update farm settings.',
      });
    }
    setLoading(false);
    setTimeout(() => {
      setSaveStatus((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  // Delete farm handler
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this farm? This action cannot be undone.')) return;
    setLoading(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('farms')
        .delete()
        .eq('id', farmId)
        .eq('user_id', user?.id);
      if (deleteError) throw deleteError;
      if (refreshFarm) await refreshFarm();
      if (onDelete) onDelete();
    } catch (err: any) {
      setError(err.message || 'Failed to delete farm.');
    }
    setLoading(false);
  };

  return (
    <DetailLayout>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Farm Settings</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={handleSave}
          sx={{ px: 4 }}
            disabled={loading}
        >
          Save Changes
        </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete Farm
          </Button>
        </Box>
      </Box>

      {/* Save Status Alert */}
      {saveStatus.show && (
        <Alert severity={saveStatus.type} sx={{ mb: 3 }}>
          {saveStatus.message}
        </Alert>
      )}

      {loading && <Alert severity="info" sx={{ mb: 2 }}>Processing...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* General Settings Section */}
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              <SettingSection>
                <Grid container spacing={2}>
                  {/* Farm Name Field */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Farm Name"
                      value={settings.general.name}
                      onChange={(e) => handleGeneralChange('name', e.target.value)}
                    />
                  </Grid>
                  {/* Location Field */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={settings.general.location}
                      onChange={(e) => handleGeneralChange('location', e.target.value)}
                    />
                  </Grid>
                  {/* Description Field */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      value={settings.general.description}
                      onChange={(e) => handleGeneralChange('description', e.target.value)}
                    />
                  </Grid>
                  {/* Farm Size Fields */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Farm Size"
                      value={settings.general.size}
                      onChange={(e) => handleGeneralChange('size', Number(e.target.value))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      select
                      label="Size Unit"
                      value={settings.general.sizeUnit}
                      onChange={(e) => handleGeneralChange('sizeUnit', e.target.value)}
                    >
                      <MenuItem value="acres">Acres</MenuItem>
                      <MenuItem value="hectares">Hectares</MenuItem>
                    </TextField>
                  </Grid>
                  {/* Timezone Field */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      select
                      label="Timezone"
                      value={settings.general.timezone}
                      onChange={(e) => handleGeneralChange('timezone', e.target.value)}
                    >
                      {timezones.map((timezone) => (
                        <MenuItem key={timezone} value={timezone}>
                          {timezone}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </SettingSection>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Notifications Section */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notifications
              </Typography>
              <SettingSection>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Notification Toggle Switches */}
                  {Object.entries(settings.notifications).map(([key, value]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={value}
                          onChange={() => handleNotificationChange(key as keyof FarmSettings['notifications'])}
                        />
                      }
                      label={key.split(/(?=[A-Z])/).join(' ')}
                    />
                  ))}
                </Box>
              </SettingSection>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Preferences Section */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preferences
              </Typography>
              <SettingSection>
                <Grid container spacing={2}>
                  {/* Currency Selection */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Currency"
                      value={settings.preferences.currency}
                      onChange={(e) => handlePreferenceChange('currency', e.target.value)}
                    >
                      {currencies.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {/* Language Selection */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Language"
                      value={settings.preferences.language}
                      onChange={(e) => handlePreferenceChange('language', e.target.value)}
                    >
                      {languages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          {language.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {/* Date Format Selection */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Date Format"
                      value={settings.preferences.dateFormat}
                      onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                    >
                      {dateFormats.map((format) => (
                        <MenuItem key={format.value} value={format.value}>
                          {format.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {/* Measurement System Selection */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Measurement System"
                      value={settings.preferences.measurementSystem}
                      onChange={(e) => handlePreferenceChange('measurementSystem', e.target.value)}
                    >
                      <MenuItem value="metric">Metric</MenuItem>
                      <MenuItem value="imperial">Imperial</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </SettingSection>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Security Settings Section */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Security Settings
              </Typography>
              <SettingSection>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Security Toggle Switches */}
                  {Object.entries(settings.security).map(([key, value]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={value}
                          onChange={() => handleSecurityChange(key as keyof FarmSettings['security'])}
                        />
                      }
                      label={key.split(/(?=[A-Z])/).join(' ')}
                    />
                  ))}
                </Box>
              </SettingSection>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Privacy Settings Section */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Privacy Settings
              </Typography>
              <SettingSection>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.privacy.isPublic}
                        onChange={(_, checked) => handlePrivacyChange('isPublic', checked)}
                      />
                    }
                    label="Public Farm Profile"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.privacy.showWorkers}
                        onChange={(_, checked) => handlePrivacyChange('showWorkers', checked)}
                      />
                    }
                    label="Show Worker List"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.privacy.showStats}
                        onChange={(_, checked) => handlePrivacyChange('showStats', checked)}
                      />
                    }
                    label="Show Farm Statistics"
                  />
                </Box>
              </SettingSection>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Storage Settings Section */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Storage & Backup
              </Typography>
              <SettingSection>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.storage.autoBackup}
                        onChange={(_, checked) => handleStorageChange('autoBackup', checked)}
                      />
                    }
                    label="Auto Backup"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Retention Days"
                    value={settings.storage.retentionDays}
                    onChange={e => handleStorageChange('retentionDays', Number(e.target.value))}
                  />
                </Box>
              </SettingSection>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </DetailLayout>
  );
};

export default FarmSettings; 